/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable eqeqeq */

import { AxiosError } from "axios";
import { styled } from "styled-components";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import api from "../../../../services/api";
import { useAppSelector } from "../../../../store/hooks";
import ModalForm from "../../../../components/modal-form";
import { TipoFormulario } from "../../../../services/constantes";
import { RelatorioCard } from "../../../../components/relatorio-card";
import { LocaSmartLayout } from "../../../../components/locasmart-layout";
import ProgressBarModal from "../../../../components/progressbar-relatorio";
import ModalEscolhaFormatoRelatorio from "../../../../components/modal-escolha-formato-relatorio";
import {
    AgrupamentoRelatorio,
    Coluna,
    ConfiguracaoBanda,
    FormatoRelatorio,
    Relatorio,
    Totalizador,
} from "../../../../components/relatorio";
import {
    obterIdTenent,
    obterNomeEmpresa,
    obterNomeUsuario,
} from "../../../../services/seguranca";
import {
    formatDateToISOWithZeroTime,
    formatarData,
    formatarParaReais,
} from "../../../../services/utilitarios";
import { ApiError, Campo } from "../../../../services/tipos";
import { obterRelatorio, TipoMovimentacaoComissao } from "../../../../services/comissao";

const ContainerRelatorioCard = styled.div`
  gap: 10px;
  width: auto;
  display: grid;
  margin: 20px;
  grid-template-columns: repeat(5, 1fr);

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 2fr));
  }
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 500;
  color: #666;
  margin-top: 20px;
  margin-left: 20px;
`;

interface UsuarioOpcao {
    id: string;
    nome: string;
}

interface ItemRelatorioComissao {
    idUsuario: string;
    nomeUsuario: string;
    tipoMovimentacao: number;
    idReferencia: string;
    idCategoria?: string | null;
    nomeCategoria?: string | null;
    idLocacao?: string | null;
    nomeLocatario?: string | null;
    dataApuracao: string;
    valorBase: number;
    valorComissao: number;
    status: string;
}

interface ItemRelatorioComissaoFormatado extends ItemRelatorioComissao {
    dataApuracaoFormatada: string;
    tipoMovimentacaoTexto: string;
    valorBaseFormatado: string;
    valorComissaoFormatado: string;
    categoriaTexto: string;
    locatarioTexto: string;
    statusTexto: string;
}

interface RelatorioComissoesProps {
    // Quando true, renderiza sem o LocaSmartLayout/título/card próprios (para uso embutido no hub Financeiro).
    embutido?: boolean;
    // Incrementado pelo pai para abrir o modal de filtro (ao clicar no card Comissões).
    abrirSinal?: number;
}

function RelatorioComissoes({ embutido = false, abrirSinal = 0 }: RelatorioComissoesProps = {}) {
    const token = useAppSelector(state => state.token);

    const [carregando, setCarregando] = useState(false);
    const [exibirContexto, setExibirContexto] = useState(false);
    const [reportTrigger, setReportTrigger] = useState(0);

    const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);
    const [dadosRelatorio, setDadosRelatorio] = useState<ItemRelatorioComissaoFormatado[]>([]);
    const [totalGeral, setTotalGeral] = useState<number>(0);
    const [totaisPorUsuario, setTotaisPorUsuario] = useState<{ idUsuario: string; nomeUsuario: string; total: number }[]>([]);

    const [dataInicial, setDataInicial] = useState<string>();
    const [dataFinal, setDataFinal] = useState<string>();
    const [idUsuarioFiltro, setIdUsuarioFiltro] = useState<string>("");
    const [tipoFiltro, setTipoFiltro] = useState<string>("");

    const [formatoRelatorio, setFormatoRelatorio] = useState<FormatoRelatorio>("pdf");
    const [exibirEscolhaFormato, setExibirEscolhaFormato] = useState(false);
    const entidadeRelatorioPendente = React.useRef<any>(null);

    const [entidadeASerAtualizada, setEntidadeASerAtualizada] = useState<any>({});
    const [forcarAtualizacao, setForcarAtualizacao] = useState(false);

    const tratarErro = useCallback((error: AxiosError<ApiError>) => {
        setCarregando(false);
        const mensagem = error.response?.data?.Mensagem || "Erro desconhecido";
        toast.error(`Não foi possível continuar a operação Relatório de Comissões: ${mensagem}`);
    }, []);

    const obterMesAnteriorAHoje = (): Date => {
        const hoje = new Date();
        hoje.setMonth(hoje.getMonth() - 1);
        return hoje;
    };
    const dataMesAnterior: Date = obterMesAnteriorAHoje();

    useEffect(() => {
        const carregarUsuarios = async () => {
            try {
                const resp = await api.get(`/UsuarioPorEmpresa/${obterIdTenent(token)}`);
                const opcoes: UsuarioOpcao[] = (resp.data || []).map((u: any) => ({
                    id: u.id,
                    nome: u.nome,
                }));
                setUsuarios(opcoes);
            } catch {
                setUsuarios([]);
            }
        };
        if (token) carregarUsuarios();
    }, [token]);

    const converterTipoMovimentacao = (tipo: number): string => {
        switch (tipo) {
            case 1: return "Pagamento";
            case 2: return "Lançamento";
            default: return "";
        }
    };

    const formatarItens = (itens: ItemRelatorioComissao[]): ItemRelatorioComissaoFormatado[] => {
        return itens.map(item => ({
            ...item,
            dataApuracaoFormatada: item.dataApuracao
                ? formatarData(new Date(item.dataApuracao))
                : "",
            tipoMovimentacaoTexto: converterTipoMovimentacao(item.tipoMovimentacao),
            valorBaseFormatado: formatarParaReais(item.valorBase),
            valorComissaoFormatado: formatarParaReais(item.valorComissao),
            categoriaTexto: (item.nomeCategoria || "").toUpperCase(),
            locatarioTexto: (item.nomeLocatario || "").toUpperCase(),
            statusTexto: (item.status || "").toUpperCase(),
        }));
    };

    const buscarRelatorio = async (contexto: {
        dataInicial: string;
        dataFinal: string;
        idUsuario?: string;
        tipoMovimentacao?: TipoMovimentacaoComissao;
    }) => {
        setCarregando(true);
        try {
            const resp = await obterRelatorio({
                dataInicio: contexto.dataInicial,
                dataFim: contexto.dataFinal,
                idUsuario: contexto.idUsuario || undefined,
                tipoMovimentacao: contexto.tipoMovimentacao,
            });

            const data = resp.data || {};
            const itens: ItemRelatorioComissao[] = data.itens || [];
            const formatados = formatarItens(itens);

            setDadosRelatorio(formatados);
            setTotalGeral(Number(data.totalGeral || 0));
            setTotaisPorUsuario((data.totaisPorUsuario || []).map((t: any) => ({
                idUsuario: t.idUsuario,
                nomeUsuario: t.nomeUsuario,
                total: Number(t.total || 0),
            })));
            setReportTrigger(prev => prev + 1);
        } catch (error: any) {
            tratarErro(error);
        } finally {
            setCarregando(false);
        }
    };

    const onSave = (e: any) => {
        if ((e["dataInicial"] && e["dataFinal"]) && (e["dataInicial"] > e["dataFinal"])) {
            toast.info("A data final deve ser maior (posterior) a data inicial.");
            return;
        }
        entidadeRelatorioPendente.current = e;
        setExibirEscolhaFormato(true);
    };

    const dispararGeracaoRelatorio = async (formato: FormatoRelatorio) => {
        setFormatoRelatorio(formato);
        setExibirEscolhaFormato(false);
        setExibirContexto(false);

        const e = entidadeRelatorioPendente.current;
        if (!e) return;
        entidadeRelatorioPendente.current = null;

        const novaDataInicial = formatarData(new Date(e["dataInicial"]));
        const novaDataFinal = formatarData(new Date(e["dataFinal"]));
        setDataInicial(novaDataInicial);
        setDataFinal(novaDataFinal);
        setIdUsuarioFiltro(e["idUsuario"] || "");
        setTipoFiltro(e["tipoMovimentacao"] ? String(e["tipoMovimentacao"]) : "");

        const idUsuarioBruto = e["idUsuario"];
        const idUsuario = (!idUsuarioBruto || idUsuarioBruto === "0" || String(idUsuarioBruto).trim() === "")
            ? undefined
            : idUsuarioBruto;

        const tipoBruto = e["tipoMovimentacao"];
        const tipoMovimentacao = (tipoBruto === 1 || tipoBruto === 2)
            ? (tipoBruto as TipoMovimentacaoComissao)
            : undefined;

        await buscarRelatorio({
            dataInicial: e["dataInicial"],
            dataFinal: e["dataFinal"],
            idUsuario,
            tipoMovimentacao,
        });
    };

    const contexto: Campo[] = [
        { coluna: 1, label: "Data Inicial", largura: 3, nome: "dataInicial", tipo: "date", linha: 1, required: true, visible: true },
        { coluna: 2, label: "Data Final", largura: 3, nome: "dataFinal", tipo: "date", linha: 1, required: true, visible: true },
        {
            coluna: 1, label: "Vendedor", largura: 6, nome: "idUsuario", tipo: "select",
            selectBoxIsCleareable: true, linha: 2, required: false, visible: true,
            opcoes: usuarios.map(u => ({ label: u.nome, valor: u.id })),
        },
        {
            coluna: 2, label: "Tipo de Movimentação", largura: 6, nome: "tipoMovimentacao",
            tipo: "select", selectBoxIsCleareable: true, linha: 2, required: false, visible: true,
            opcoes: [
                { label: "Pagamento", valor: 1 },
                { label: "Lançamento", valor: 2 },
            ],
        },
    ];

    const onOpenContexto = async () => {
        setEntidadeASerAtualizada({
            dataInicial: formatDateToISOWithZeroTime(dataMesAnterior),
            dataFinal: formatDateToISOWithZeroTime(new Date()),
            idUsuario: null,
            tipoMovimentacao: null,
        });
        setForcarAtualizacao(true);
    };

    useEffect(() => {
        if (forcarAtualizacao) {
            setForcarAtualizacao(false);
        }
    }, [forcarAtualizacao]);

    const handleAplicarFiltro = () => {
        setDadosRelatorio([]);
        setTotalGeral(0);
        setTotaisPorUsuario([]);
        setDataInicial(undefined);
        setDataFinal(undefined);
        setIdUsuarioFiltro("");
        setTipoFiltro("");
        setExibirContexto(true);
    };

    // Quando embutido no hub Financeiro, abrir o modal de filtro ao clicar no card (sinal do pai).
    useEffect(() => {
        if (embutido && abrirSinal > 0) {
            handleAplicarFiltro();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abrirSinal]);

    const colunasRelatorio: Coluna[] = [
        { propriedade: "dataApuracaoFormatada", titulo: "Data", largura: 22 },
        { propriedade: "tipoMovimentacaoTexto", titulo: "Tipo", largura: 22 },
        { propriedade: "categoriaTexto", titulo: "Categoria", largura: 35 },
        { propriedade: "locatarioTexto", titulo: "Locatário", largura: 50 },
        { propriedade: "valorBaseFormatado", titulo: "Vr Base", largura: 25, alinhamento: "right" },
        { propriedade: "valorComissaoFormatado", titulo: "Comissão", largura: 25, alinhamento: "right" },
        { propriedade: "statusTexto", titulo: "Status", largura: 25 },
    ];

    const totalizadoresAgrupamento: Totalizador[] = [
        {
            coluna: "valorComissaoFormatado",
            label: "Subtotal Vendedor",
            calculo: (d: any[]) => formatarParaReais(
                d.reduce((acc, x) => acc + (x.valorComissao || 0), 0),
            ),
        },
    ];

    const agrupamentoRelatorio: AgrupamentoRelatorio = {
        campoTitulo: "nomeUsuario",
        tituloVazio: "Sem Vendedor",
        totalizadores: totalizadoresAgrupamento,
    };

    const configuracaoBanda: ConfiguracaoBanda = {
        mestre: {
            colunas: colunasRelatorio,
            totalizadores: [
                {
                    coluna: "valorComissaoFormatado",
                    label: "TOTAL",
                    calculo: (d: any[]) => formatarParaReais(
                        d.reduce((acc, x) => acc + (x.valorComissao || 0), 0),
                    ),
                },
            ],
        },
    };

    const filtroVendedorTexto = idUsuarioFiltro
        ? usuarios.find(u => u.id === idUsuarioFiltro)?.nome || "TODOS"
        : "TODOS";
    const filtroTipoTexto = tipoFiltro
        ? converterTipoMovimentacao(Number(tipoFiltro))
        : "TODOS";

    const subTituloRelatorio = (dataInicial && dataFinal)
        ? `Período: ${dataInicial} à ${dataFinal} | Vendedor: ${filtroVendedorTexto} | Tipo: ${filtroTipoTexto}`
        : "";

    const rodapeRelatorio: string[] = [
        `Total Geral em Comissões: ${formatarParaReais(totalGeral)}`,
        ...(totaisPorUsuario.length > 0
            ? totaisPorUsuario.map(t => `Total ${t.nomeUsuario}: ${formatarParaReais(t.total)}`)
            : []),
    ];

    const conteudo = (
        <>
            <Relatorio
                trigger={reportTrigger}
                configuracaoBanda={configuracaoBanda}
                fonteDeDados={dadosRelatorio}
                titulo="Comissões"
                subtitulo={subTituloRelatorio}
                usuario={obterNomeUsuario(token)}
                rodape={rodapeRelatorio}
                empresa={obterNomeEmpresa(token)}
                formato={formatoRelatorio}
                agrupamento={agrupamentoRelatorio}
            />

            <ModalForm
                campos={contexto}
                onShow={onOpenContexto}
                carregando={false}
                modo={TipoFormulario.tpCadastro}
                titulo="Gerar Relatório: Comissões"
                tituloBotaoOk="Gerar"
                apiConfig={{
                    endpoint: "",
                    idTenentCampo: "idEmpresa",
                    requisicaoBemSucedida: () => { toast.success("Relatório gerado com sucesso"); },
                    tratarErro: () => { },
                }}
                onSave={onSave}
                onCancel={() => setExibirContexto(false)}
                onHide={() => setExibirContexto(false)}
                show={exibirContexto}
                tamanho="lg"
                atualizacaoEntidade={{
                    entidadeUsadaParaAtualizacao: entidadeASerAtualizada,
                    forcarAtualizacao: forcarAtualizacao,
                }}
            />

            <ProgressBarModal
                carregando={carregando}
                titulo="Carregando relatório..."
            />

            <ModalEscolhaFormatoRelatorio
                open={exibirEscolhaFormato}
                onClose={() => setExibirEscolhaFormato(false)}
                onConfirmar={dispararGeracaoRelatorio}
            />
        </>
    );

    if (embutido) {
        return conteudo;
    }

    return (
        <LocaSmartLayout>
            <Title>Financeiro:</Title>

            <ContainerRelatorioCard>
                <RelatorioCard
                    titulo="Comissões"
                    subtitulo="Relatório de comissões apuradas por vendedor."
                    onAplicarFiltros={handleAplicarFiltro}
                    corBotao="#1F467E"
                />
            </ContainerRelatorioCard>

            {conteudo}
        </LocaSmartLayout>
    );
}

export default RelatorioComissoes;
